import { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Field } from '@/components/ui/Input';
import { Slider } from '@/components/ui/Slider';
import { Dropzone } from '@/components/ui/Dropzone';
import { Badge } from '@/components/ui/Badge';
import { useApp } from '@/store/app';
import { loadImage, readFileAsDataURL } from '@/lib/utils';
import { processAndSave, canvasToBlob, stripExt } from '@/lib/bulk';

type Fmt = 'png' | 'jpeg' | 'webp';

export function ImageConvert() {
  const { pendingFile, setPendingFile } = useApp();
  const [files, setFiles] = useState<File[]>([]);
  const [format, setFormat] = useState<Fmt>('webp');
  const [quality, setQuality] = useState(90);
  const [working, setWorking] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  useEffect(() => {
    if (pendingFile && pendingFile.type.startsWith('image/')) {
      setFiles([pendingFile]);
      setPendingFile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function run() {
    if (!files.length) return;
    setWorking(true);
    setProgress({ done: 0, total: files.length });
    try {
      const ext = format === 'jpeg' ? 'jpg' : format;
      await processAndSave(
        files,
        async (file) => {
          const url = await readFileAsDataURL(file);
          const img = await loadImage(url);
          const c = document.createElement('canvas');
          c.width = img.width;
          c.height = img.height;
          const ctx = c.getContext('2d')!;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0);
          const blob = await canvasToBlob(c, `image/${format}`, quality / 100);
          return { blob, filename: `${stripExt(file.name)}.${ext}` };
        },
        {
          zipName: `converted-${ext}`,
          onProgress: (done, total) => setProgress({ done, total }),
        },
      );
    } finally {
      setWorking(false);
      setProgress({ done: 0, total: 0 });
    }
  }

  return (
    <div>
      <ToolHeader
        toolId="image-convert"
        title="Image format converter"
        description="Convert PNG ↔ JPEG ↔ WebP. Multiple files are packaged into a ZIP."
        badges={files.length > 1 ? <Badge variant="accent">{files.length} files</Badge> : null}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <Dropzone
            accept="image/*"
            multiple
            onFiles={(f) => setFiles((p) => [...p, ...f])}
            files={files}
            onRemove={(i) => setFiles((p) => p.filter((_, idx) => idx !== i))}
            hint="PNG, JPG, WebP, GIF — drop one or many"
          />
        </Card>
        <Card className="space-y-4">
          <Field label="Output format">
            <Select
              value={format}
              onChange={(e) => setFormat(e.target.value as Fmt)}
              options={[
                { value: 'webp', label: 'WebP' },
                { value: 'png', label: 'PNG' },
                { value: 'jpeg', label: 'JPEG' },
              ]}
            />
          </Field>
          {format !== 'png' && (
            <Field label="Quality" hint={`${quality}%`}>
              <Slider value={quality} onChange={setQuality} min={10} max={100} showValue={false} />
            </Field>
          )}
          <Button
            variant="primary"
            className="w-full"
            disabled={!files.length || working}
            loading={working}
            onClick={run}
          >
            <Download size={12} />
            {files.length > 1 ? `Convert ${files.length} → ZIP` : files.length === 1 ? 'Convert' : 'Convert'}
          </Button>
          {working && progress.total > 1 && (
            <div className="text-2xs text-fg-subtle">
              Processing {progress.done} / {progress.total}…
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

export default ImageConvert;
